import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, X } from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import {
  traiComplianceService, DeclaredNumber, DeclarationReport, A2pCategory,
} from 'api/services/traiCompliance.service';
import { CallCategorySelect } from 'components/Compliance/CallCategoryField';
import { COMPLIANCE_ADMINS, SERIES_LABEL, describeIssue, formatWhen, hasRole, localInputToIso } from './traiRules';
import { Card, ImportPanel, Note, Pill, inputCls, labelCls, muted, th, td } from './shared';

/**
 * Item 2: A2P / DLT declarations on the organisation's numbers, and the report of numbers used
 * for automated outbound in the last N days that are undeclared or on the wrong series.
 * Declaring changes no call today: the A2P gate runs in shadow.
 */
const NumbersTab: React.FC = () => {
  const { currentOrganization, user } = useAuth();
  const role = (currentOrganization?.role || user?.role)?.toLowerCase();
  const canEdit = hasRole(role, COMPLIANCE_ADMINS);

  const [numbers, setNumbers] = useState<DeclaredNumber[]>([]);
  const [report, setReport] = useState<DeclarationReport | null>(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [editing, setEditing] = useState<DeclaredNumber | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [n, r] = await Promise.all([traiComplianceService.numbers(), traiComplianceService.report(days)]);
    if (n.success) setNumbers(Array.isArray(n.data) ? n.data : []);
    else setNote({ ok: false, text: n.error || 'Could not load numbers' });
    if (r.success && r.data) setReport(r.data);
    setLoading(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const clear = async (n: DeclaredNumber) => {
    if (!window.confirm(`Clear the A2P declaration on ${n.e164}?`)) return;
    const r = await traiComplianceService.clearDeclaration(n.id);
    setNote(r.success ? { ok: true, text: `Cleared ${n.e164}` } : { ok: false, text: r.error || 'Could not clear' });
    load();
  };

  return (
    <div className="space-y-4">
      <Note note={note} />

      <Card title={`Automated outbound — last ${days} days`}
            subtitle="Numbers that placed automated calls, and what is wrong with their declaration. Read-only."
            right={(
              <div className="flex items-center gap-2">
                <select className="tl-input" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                  {[7, 30, 90].map((d) => <option key={d} value={d}>{d} days</option>)}
                </select>
                <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={load}>
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </button>
              </div>
            )}>
        {!report ? <p className="text-sm" style={muted}>{loading ? 'Loading…' : 'No report.'}</p> : (
          <>
            <p className="text-sm">
              <b>{report.numbersWithIssues}</b> of <b>{report.numbersUsed}</b> numbers used have issues.
              {' '}A2P gate: {report.a2pGateEnforcing ? <Pill tone="danger">enforcing</Pill> : <Pill tone="info">shadow</Pill>}
              {report.auditStartsAt
                ? <span style={muted}> Per-call audit since {formatWhen(report.auditStartsAt)}; earlier calls from campaign and API records.</span>
                : <span style={muted}> No per-call audit yet; counted from campaign and API records.</span>}
            </p>
            {report.rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr>
                    <th className={th}>Number</th><th className={th}>Calls</th><th className={th}>Series</th>
                    <th className={th}>Declared</th><th className={th}>Issues</th>
                  </tr></thead>
                  <tbody>
                    {report.rows.map((r) => (
                      <tr key={r.cli} className="border-t border-gray-100 dark:border-gray-800">
                        <td className={`${td} font-mono`}>{r.cli}</td>
                        <td className={td}>
                          {r.calls}
                          <div className="text-[11px]" style={muted}>
                            {Object.entries(r.callsByCategory).map(([k, v]) => `${k.toLowerCase()} ${v}`).join(', ')}
                          </div>
                        </td>
                        <td className={td}>{r.series ? SERIES_LABEL[r.series] || r.series : r.regime === 'NONE' ? 'Not Indian (no regime)' : '—'}</td>
                        <td className={td}>{r.declared ? <Pill tone="success">{r.declaredCategory || 'declared'}</Pill> : <Pill tone="amber">no</Pill>}</td>
                        <td className={td}>
                          {r.issues.length === 0 ? <span style={muted}>None</span> : (
                            <ul className="text-xs list-disc pl-4">{r.issues.map((i) => <li key={i}>{describeIssue(i)}</li>)}</ul>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </Card>

      <Card title="This organisation's numbers" subtitle="Declaration status, series (read from the digits) and category.">
        {numbers.length === 0 ? <p className="text-sm" style={muted}>{loading ? 'Loading…' : 'No numbers.'}</p> : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr>
                <th className={th}>Number</th><th className={th}>Series</th><th className={th}>Declared</th>
                <th className={th}>Reference / DLT</th><th className={th}>Category</th><th className={th}>Warnings</th>
                {canEdit && <th className={th} />}
              </tr></thead>
              <tbody>
                {numbers.map((n) => (
                  <tr key={n.id} className="border-t border-gray-100 dark:border-gray-800">
                    <td className={`${td} font-mono`}>{n.e164}<div className="text-[11px]" style={muted}>{n.status}</div></td>
                    <td className={td}>{n.series ? SERIES_LABEL[n.series] || n.series : n.effectiveRegime === 'NONE' ? 'No regime' : '—'}</td>
                    <td className={td}>{n.declared ? formatWhen(n.declaredAt) : <Pill tone="amber">Undeclared</Pill>}</td>
                    <td className={td}>{n.declaredRef || '—'}</td>
                    <td className={td}>{n.category || '—'}</td>
                    <td className={td}>{n.warnings.length ? <ul className="text-xs list-disc pl-4">{n.warnings.map((w) => <li key={w}>{describeIssue(w)}</li>)}</ul> : '—'}</td>
                    {canEdit && (
                      <td className={`${td} whitespace-nowrap`}>
                        <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={() => setEditing(n)}>Declare</button>
                        {n.declared && <button className="tl-btn tl-btn-secondary tl-btn-sm ml-1" onClick={() => clear(n)}>Clear</button>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && <DeclareForm n={editing} onClose={() => setEditing(null)} onSaved={(t) => { setEditing(null); setNote({ ok: true, text: t }); load(); }} />}

      {canEdit && (
        <Card title="Import declarations (CSV)" subtitle="Always previewed first. action = SET (default) or CLEAR.">
          <ImportPanel
            columns="e164,declared_at,reference,category,regime,action"
            example="+912265197304,2026-11-01,DLT-123,SERVICE,,SET"
            run={traiComplianceService.importDeclarations}
            onApplied={load}
            renderPreview={(r) => (
              <div className="overflow-x-auto max-h-72">
                <table className="w-full">
                  <thead><tr><th className={th}>Line</th><th className={th}>Number</th><th className={th}>Action</th><th className={th}>Before → after</th><th className={th}>Warnings</th></tr></thead>
                  <tbody>
                    {(r.results || []).filter((x) => !x.error).slice(0, 100).map((x) => (
                      <tr key={x.line} className="border-t border-gray-100 dark:border-gray-800">
                        <td className={td}>{x.line}</td>
                        <td className={`${td} font-mono`}>{x.e164}</td>
                        <td className={td}>{x.action}</td>
                        <td className={`${td} text-xs`}>{x.before?.category || '—'} / {x.before?.declaredRef || '—'} → {x.after?.category || '—'} / {x.after?.declaredRef || '—'}</td>
                        <td className={`${td} text-xs`}>{(x.warnings || []).map(describeIssue).join('; ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          />
        </Card>
      )}
    </div>
  );
};

const DeclareForm: React.FC<{ n: DeclaredNumber; onClose: () => void; onSaved: (t: string) => void }> = ({ n, onClose, onSaved }) => {
  const [declaredAt, setDeclaredAt] = useState('');
  const [reference, setReference] = useState(n.declaredRef || '');
  const [category, setCategory] = useState<'' | A2pCategory>((n.category || '') as '' | A2pCategory);
  const [regime, setRegime] = useState(n.regime || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const save = async () => {
    setBusy(true); setErr(null);
    const r = await traiComplianceService.setDeclaration(n.id, {
      declaredAt: localInputToIso(declaredAt), reference: reference || undefined,
      category: category || undefined, regime: regime || undefined,
    });
    setBusy(false);
    if (r.success) onSaved(`Declared ${n.e164}`);
    else setErr(r.error || 'Could not save');
  };

  return (
    <Card title={`Declare ${n.e164}`} subtitle="As recorded with the originating access provider / on DLT."
          right={<button className="tl-icon-btn" onClick={onClose} aria-label="Close"><X className="w-4 h-4" /></button>}>
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <label><span className={labelCls} style={muted}>Declared at (blank = now)</span>
          <input type="datetime-local" className={inputCls} value={declaredAt} onChange={(e) => setDeclaredAt(e.target.value)} /></label>
        <label><span className={labelCls} style={muted}>Reference / DLT id</span>
          <input className={inputCls} value={reference} maxLength={128} onChange={(e) => setReference(e.target.value)} /></label>
        <label><span className={labelCls} style={muted}>Category</span>
          <CallCategorySelect value={category} onChange={setCategory} className={inputCls} /></label>
        <label><span className={labelCls} style={muted}>Regime</span>
          <select className={inputCls} value={regime} onChange={(e) => setRegime(e.target.value)}>
            <option value="">From the number</option><option value="TRAI_IN">TRAI_IN</option><option value="NONE">NONE</option>
          </select></label>
      </div>
      {category === 'PROMOTIONAL' && n.series && n.series !== 'PROMOTIONAL_140' && (
        <p className="text-xs text-amber-700">Promotional calls must come from the 140 series; this is {SERIES_LABEL[n.series] || n.series}.</p>
      )}
      {err && <p className="text-sm text-red-700">{err}</p>}
      <div className="flex gap-2">
        <button className="tl-btn tl-btn-primary tl-btn-sm" disabled={busy} onClick={save}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save declaration'}
        </button>
        <button className="tl-btn tl-btn-secondary tl-btn-sm" onClick={onClose}>Cancel</button>
      </div>
    </Card>
  );
};

export default NumbersTab;
